import React, { useEffect, useState, useRef } from 'lib/react';
import { EVSearch } from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import PropTypes from 'prop-types';
import { MATCH_NOT_FOUND } from 'constants/AppConstants';
import useMediaQuery from 'hooks/useMediaQuery';
import TypeAheadSearchService from 'services/typeAheadSearch.service';
import history from 'store/CreateHistory';
import { ORDER_ROUTE, PLACE_ORDER_ROUTE } from 'constants/RouteConstants';
import { useLocation } from 'lib/router';
import InputField from './InputField.component';
import LocationTemplate from './LocationTemplate.component';
import OrderStyles from '../OrderStyles';
import { LATITUDE, LONGITUDE, ADDRESS } from '../../../constants/AppConstants';
import { fetchGeocodedAddress, updateAddressSearchText, setPanelType } from '../Order.actions';
import { PANEL_TYPES } from '../Order.constants';

/**
 * Component for searching address and getting autocomplete suggestions.
 */
function TypeAheadSearch(props) {
  const { suggestThreshold, maxListSize } = props;
  const [data, setData] = useState([]);
  const country = useSelector(({ order }) => order.country);
  const { addressSearchText, isOrderFlowStarted, uploadingBluePrintFile } = useSelector(({ orderAddresses, order }) => ({
    addressSearchText: orderAddresses.addressSearchText,
    isOrderFlowStarted: order.isOrderFlowStarted,
    uploadingBluePrintFile: order.uploadingBluePrintFile,
  }));
  const dispatch = useDispatch();
  const classes = OrderStyles();
  const isMobile = useMediaQuery(640);
  const { pathname } = useLocation();

  const setInputValue = (inputText) => dispatch(updateAddressSearchText(inputText, isOrderFlowStarted));

  const debounceRef = useRef(null);
  const handleSearchTermChanged = (_, searchString) => {
    setInputValue(searchString);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (searchString.length >= suggestThreshold) {
        TypeAheadSearchService(searchString, country)
          .then((response) => {
            if (response.ok) setData(response.json);
          });
      }
    }, 300);
  };

  const filterOptions = (options) => {
    // render suggestion list if input length is grater than or equal to suggestThreshold.
    if (addressSearchText.length < suggestThreshold) return [];
    if (addressSearchText.length >= suggestThreshold && options.length === 0) return ['no result found'];
    // returns the first {maxListSize} options from options list.
    return options.slice(0, maxListSize);
  };

  const fetchAddress = (searchInput) => {
    dispatch(fetchGeocodedAddress(searchInput, country));
    dispatch(setPanelType(PANEL_TYPES.PRE_PANEL));
  };

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const address = urlSearchParams.get(ADDRESS);
    const lat = urlSearchParams.get(LATITUDE);
    const long = urlSearchParams.get(LONGITUDE);

    let searchText = '';

    if (address) {
      searchText = address;
    } else if (lat || long) {
      searchText = `${lat || ''},${long || ''}`;
    }

    if (searchText) setInputValue(searchText);
    if (searchText.length >= suggestThreshold) fetchAddress(searchText);
  }, []);

  const handleRenderOption = (option) => ((typeof option !== 'string') ? <LocationTemplate option={option} /> : MATCH_NOT_FOUND);

  const handleFocus = () => (
    (![ORDER_ROUTE, PLACE_ORDER_ROUTE].includes(pathname))
    && !uploadingBluePrintFile
    && history.push(ORDER_ROUTE)
  );

  return (
    <EVSearch
      id="address-search"
      data-testid="address-search"
      disablePortal
      defaultValue={addressSearchText}
      inputValue={addressSearchText}
      onInputChange={handleSearchTermChanged}
      filterOptions={filterOptions}
      data={data}
      renderInput={InputField}
      renderOption={handleRenderOption}
      onResultSelected={fetchAddress}
      enableEnterOnCustomSearch
      onCustomResultSelected={fetchAddress}
      noAutoSuggestResultMessage={{}}
      classes={{ root: classes.autocompleteRoot, popper: isMobile ? classes.autocompletePopper : '' }}
      blurOnSelect
      onFocus={handleFocus}
    />
  );
}

TypeAheadSearch.propTypes = {
  /**
   * It is used to set the minimum characters required to show the list of results
   */
  suggestThreshold: PropTypes.number,
  /**
   * It is used to set the number of type ahead results to be shown
   */
  maxListSize: PropTypes.number,
};

TypeAheadSearch.defaultProps = {
  maxListSize: 5,
  suggestThreshold: 3,
};

export default TypeAheadSearch;
